import { ref, computed, type Ref } from "vue";
import { defineStore } from "pinia";
import type { Client, Installation, User } from "@/models";
import API from "@/api";

export const useUserStore = defineStore("user", () => {
	const user = ref() as Ref<User>;
	const isLoggedIn = ref(localStorage.getItem("loggedIn") == "true");
	const selectedClient = ref(localStorage.getItem("selectedClient"));
	const clients = ref<Client[]>([]);

	async function loadUser(): Promise<User> {
		await API.get<User>("api/v1/user/me").then((x) => (user.value = x.data));

		return user.value;
	}

	function setSelectedClient(client_id?: number) {
		if (!client_id) {
			localStorage.removeItem("selectedClient");
			return;
		}
		localStorage.setItem("selectedClient", client_id.toString());

		selectedClient.value = client_id.toString();
	}

	function setClients(newClients: Client[]) {
		clients.value = newClients;
	}

	const scope = computed(() => {
		const u = user.value;
		if (!u?.scope_type || !u.scope_id) return null;
		const isInstallation = u.scope_type.endsWith("Installation");
		const locationId = isInstallation ? (u.scope as Installation | null | undefined)?.location_id : u.scope_id;
		if (!locationId) return null;
		return {
			type: isInstallation ? ("installation" as const) : ("location" as const),
			locationId,
			installationId: isInstallation ? u.scope_id : null,
			title: u.scope?.title ?? `#${u.scope_id}`,
		};
	});

	async function login(email: string, password: string, remember = false): Promise<string | undefined> {
		await API.raw().get("/sanctum/csrf-cookie");
		await API.raw().post("/login", {
			email,
			password,
			remember,
		});
		localStorage.setItem("loggedIn", "true");
		// Mirror into the ref like logout() does — login is a router navigation, so no reload resets it.
		isLoggedIn.value = true;

		await loadUser().then((user) => {
			if (user.roles.includes("moos:admin")) setSelectedClient(-1);
			else setSelectedClient(user.client_id);
		});

		return undefined;
	}

	async function logout() {
		isLoggedIn.value = false;
		user.value = undefined as unknown as User;
		localStorage.clear();

		return API.raw().post("/logout");
	}

	return {
		user,
		isLoggedIn,
		logout,
		login,
		loadUser,
		selectedClient,
		setSelectedClient,
		clients,
		setClients,
		scope,
	};
});
